// iconLibrary.js
//
// Stocked LINE-ART icon library for the Segment icon picker — implements deck
// concept `design-handoff/concepts/procedural-libraries/04-icon-library.html`
// (Decision #35, Gordon 2026-07-02) and the production sourcing decision in
// `design-handoff/ICON-SOURCES.md` (primary set = Tabler Icons, MIT, line-art,
// 24x24 / 2px stroke).
//
// Every glyph here is an original Tabler line-art SVG (stroke, fill="none") —
// NEVER an emoji/raster. Glyphs are grouped into the deck's 13 categories
// (Essentials, Arrows, Business, Nature, Animals, Food, Travel, Health, Tech,
// Social, Weather, Objects, Symbols) so the picker's category tabs read exactly
// as the deck. The picker (SegmentIcon.jsx) consumes this map behind the
// ICON_LIBRARY_V2_ENABLED flag; when the flag is OFF it keeps using the legacy
// `faCategories` map and this module's icons are never rendered.
//
// Shape mirrors faCategories: { [categoryLabel]: WrappedIconComponent[] }, each
// component carrying a `.displayName` (its Tabler name) that the picker's search
// box matches against. Selection stores <IconComponent /> via the SAME
// onIconSelect path as the legacy set — no new data field, no backend contract.
import {
  TbHome,
  TbStar,
  TbHeart,
  TbBookmark,
  TbSettings,
  TbBell,
  TbSearch,
  TbUser,
  TbCalendarEvent,
  TbClock,
  TbCheck,
  TbFlag,
  TbEye,
  TbTrash,
  TbEdit,
  TbPlus,
  TbArrowUp,
  TbArrowDown,
  TbArrowLeft,
  TbArrowRight,
  TbArrowUpRight,
  TbArrowBackUp,
  TbArrowForwardUp,
  TbChevronUp,
  TbChevronDown,
  TbChevronRight,
  TbRefresh,
  TbArrowsShuffle,
  TbTarget,
  TbArrowLoopRight,
  TbBriefcase,
  TbBuilding,
  TbChartBar,
  TbChartLine,
  TbChartPie,
  TbCoin,
  TbCreditCard,
  TbCurrencyDollar,
  TbReceipt,
  TbPresentation,
  TbBuildingBank,
  TbWallet,
  TbTrendingUp,
  TbReportMoney,
  TbTree,
  TbLeaf,
  TbFlower,
  TbPlant,
  TbSeeding,
  TbMountain,
  TbSun,
  TbMoon,
  TbFlame,
  TbDroplet,
  TbSnowflake,
  TbWorld,
  TbCactus,
  TbClover,
  TbCat,
  TbDog,
  TbFish,
  TbBat,
  TbButterfly,
  TbDeer,
  TbHorse,
  TbPig,
  TbSpider,
  TbFeather,
  TbPaw,
  TbBug,
  TbCoffee,
  TbCup,
  TbGlass,
  TbPizza,
  TbBurger,
  TbIceCream,
  TbCake,
  TbApple,
  TbCarrot,
  TbBread,
  TbEgg,
  TbMeat,
  TbSalad,
  TbSoup,
  TbPlane,
  TbCar,
  TbBus,
  TbTrain,
  TbBike,
  TbShip,
  TbMap,
  TbMapPin,
  TbLuggage,
  TbBackpack,
  TbCompass,
  TbTent,
  TbBeach,
  TbRoad,
  TbHeartbeat,
  TbStethoscope,
  TbPill,
  TbVaccine,
  TbFirstAidKit,
  TbActivity,
  TbYoga,
  TbRun,
  TbWeight,
  TbMedicalCross,
  TbDental,
  TbMoodHappy,
  TbDeviceLaptop,
  TbDeviceMobile,
  TbDeviceDesktop,
  TbCpu,
  TbDatabase,
  TbCloud,
  TbWifi,
  TbBluetooth,
  TbCode,
  TbTerminal,
  TbRobot,
  TbDeviceWatch,
  TbUsb,
  TbServer,
  TbBrandTwitter,
  TbBrandInstagram,
  TbBrandFacebook,
  TbBrandYoutube,
  TbBrandLinkedin,
  TbBrandTiktok,
  TbBrandGithub,
  TbBrandWhatsapp,
  TbBrandDiscord,
  TbBrandSnapchat,
  TbBrandPinterest,
  TbMessageCircle,
  TbShare,
  TbThumbUp,
  TbCloudRain,
  TbCloudSnow,
  TbCloudStorm,
  TbWind,
  TbUmbrella,
  TbTemperature,
  TbBolt,
  TbRainbow,
  TbMist,
  TbSunset,
  TbSunrise,
  TbCloudFog,
  TbCamera,
  TbBook,
  TbGift,
  TbKey,
  TbLock,
  TbBulb,
  TbBrush,
  TbPalette,
  TbScissors,
  TbPaperclip,
  TbPin,
  TbTrophy,
  TbLamp,
  TbHanger,
  TbCircle,
  TbSquare,
  TbTriangle,
  TbHexagon,
  TbDiamond,
  TbInfinity,
  TbX,
  TbMinus,
  TbQuestionMark,
  TbExclamationMark,
  TbPlusMinus,
  TbEqual,
  TbPercentage,
  TbHash,
  // Icon-picker-expand (2026-08-20, ISP-9999): +104 more Tabler line-art
  // glyphs (SAME set/weight as every icon above — 24x24 / 2px stroke, no
  // family mixing) so every subject-category tab (People, Fitness & Sports,
  // Shopping, Media & Music, Education, Text & Editing, ...) has more than a
  // couple of items instead of 1-2. See SUBJECTS in
  // SegmentBuilder/Shared/SegmentStyling/logic/iconSubjects.js for the
  // keyword taxonomy each of these is chosen to land in.
  TbUsers,
  TbUserCircle,
  TbUserPlus,
  TbUserX,
  TbUserCheck,
  TbUserSearch,
  TbUserShield,
  TbUserStar,
  TbUserEdit,
  TbFriends,
  TbBabyCarriage,
  TbBabyBottle,
  TbGenderMale,
  TbGenderFemale,
  TbMoodSmile,
  TbMoodSad,
  TbMoodWink,
  TbMoodAngry,
  TbHandStop,
  TbBallBasketball,
  TbBallBaseball,
  TbBallFootball,
  TbBallTennis,
  TbBallVolleyball,
  TbBallAmericanFootball,
  TbBallBowling,
  TbGolf,
  TbSwimming,
  TbSkateboard,
  TbBarbell,
  TbMedal,
  TbWalk,
  TbStretching,
  TbChess,
  TbDice,
  TbAward,
  TbPuzzle,
  TbBasket,
  TbBuildingStore,
  TbDiscount,
  TbShoppingBag,
  TbShoppingCart,
  TbTag,
  TbTags,
  TbBarcode,
  TbTicket,
  TbQrcode,
  TbBuildingWarehouse,
  TbHeadphones,
  TbMicrophone,
  TbMovie,
  TbMusic,
  TbPhoto,
  TbVideo,
  TbPlayerPlay,
  TbPlayerPause,
  TbVolume,
  TbBroadcast,
  TbDeviceTv,
  TbPlaylist,
  TbFileMusic,
  TbDisc,
  TbSchool,
  TbAbacus,
  TbAtom,
  TbFlask,
  TbMicroscope,
  TbPencil,
  TbRuler,
  TbCertificate,
  TbBooks,
  TbLanguage,
  TbNotebook,
  TbBold,
  TbItalic,
  TbUnderline,
  TbStrikethrough,
  TbAlignLeft,
  TbAlignCenter,
  TbList,
  TbQuote,
  TbHighlight,
  TbTypography,
  TbSubscript,
  TbSuperscript,
  TbBandage,
  TbThermometer,
  TbVirus,
  TbBrain,
  TbAmbulance,
  TbWheelchair,
  TbDoor,
  TbBed,
  TbBath,
  TbSofa,
  TbPlug,
  TbHammer,
  TbTools,
  TbWindow,
  TbBoxSeam,
  TbPaint,
  TbHourglass,
  TbAlarm,
  TbMail,
  TbPhoneCall,
  TbAt,
  TbFolder,
  TbFile,
  TbClipboard,
  TbCoins,
  TbCalculator,
  TbPrinter,
  TbNumber0,
  TbNumber1,
  TbNumber2,
  TbNumber3,
  TbNumber4,
  TbNumber5,
  TbNumber6,
  TbNumber7,
  TbNumber8,
  TbNumber9,
  TbCircleNumber0,
  TbCircleNumber1,
  TbCircleNumber2,
  TbCircleNumber3,
  TbCircleNumber4,
  TbCircleNumber5,
  TbCircleNumber6,
  TbCircleNumber7,
  TbCircleNumber8,
  TbCircleNumber9,
  TbSquareNumber0,
  TbSquareNumber1,
  TbSquareNumber2,
  TbSquareNumber3,
  TbSquareNumber4,
  TbSquareNumber5,
  TbSquareNumber6,
  TbSquareNumber7,
  TbSquareNumber8,
  TbSquareNumber9,
} from "react-icons/tb";
import { wrapIconWithDisplayName } from "./faCategories";

// Local alias so each entry stays one readable line. Reuses faCategories'
// wrapper (shared component) so both sets carry a matching displayName contract.
const w = (Icon, name) => wrapIconWithDisplayName(Icon, name);

// Deck's 13 categories, in deck order. Keys are the human labels the picker
// renders as category tabs.
const iconLibraryCategories = {
  // T22.90 (Kelly 2026-10-10: "the most important segment icons would be
  // numbers"): 0-9 plain, in a circle, in a square. The Tabler set has no
  // two-digit glyphs (no 10-20), so numbers stop at 9.
  Numbers: [
    w(TbNumber0, "TbNumber0"),
    w(TbNumber1, "TbNumber1"),
    w(TbNumber2, "TbNumber2"),
    w(TbNumber3, "TbNumber3"),
    w(TbNumber4, "TbNumber4"),
    w(TbNumber5, "TbNumber5"),
    w(TbNumber6, "TbNumber6"),
    w(TbNumber7, "TbNumber7"),
    w(TbNumber8, "TbNumber8"),
    w(TbNumber9, "TbNumber9"),
    w(TbCircleNumber0, "TbCircleNumber0"),
    w(TbCircleNumber1, "TbCircleNumber1"),
    w(TbCircleNumber2, "TbCircleNumber2"),
    w(TbCircleNumber3, "TbCircleNumber3"),
    w(TbCircleNumber4, "TbCircleNumber4"),
    w(TbCircleNumber5, "TbCircleNumber5"),
    w(TbCircleNumber6, "TbCircleNumber6"),
    w(TbCircleNumber7, "TbCircleNumber7"),
    w(TbCircleNumber8, "TbCircleNumber8"),
    w(TbCircleNumber9, "TbCircleNumber9"),
    w(TbSquareNumber0, "TbSquareNumber0"),
    w(TbSquareNumber1, "TbSquareNumber1"),
    w(TbSquareNumber2, "TbSquareNumber2"),
    w(TbSquareNumber3, "TbSquareNumber3"),
    w(TbSquareNumber4, "TbSquareNumber4"),
    w(TbSquareNumber5, "TbSquareNumber5"),
    w(TbSquareNumber6, "TbSquareNumber6"),
    w(TbSquareNumber7, "TbSquareNumber7"),
    w(TbSquareNumber8, "TbSquareNumber8"),
    w(TbSquareNumber9, "TbSquareNumber9"),
  ],
  Essentials: [
    w(TbHome, "TbHome"),
    w(TbStar, "TbStar"),
    w(TbHeart, "TbHeart"),
    w(TbBookmark, "TbBookmark"),
    w(TbSettings, "TbSettings"),
    w(TbBell, "TbBell"),
    w(TbSearch, "TbSearch"),
    w(TbUser, "TbUser"),
    w(TbCalendarEvent, "TbCalendarEvent"),
    w(TbClock, "TbClock"),
    w(TbCheck, "TbCheck"),
    w(TbFlag, "TbFlag"),
    w(TbEye, "TbEye"),
    w(TbTrash, "TbTrash"),
    w(TbEdit, "TbEdit"),
    w(TbPlus, "TbPlus"),
  ],
  Arrows: [
    w(TbArrowUp, "TbArrowUp"),
    w(TbArrowDown, "TbArrowDown"),
    w(TbArrowLeft, "TbArrowLeft"),
    w(TbArrowRight, "TbArrowRight"),
    w(TbArrowUpRight, "TbArrowUpRight"),
    w(TbArrowBackUp, "TbArrowBackUp"),
    w(TbArrowForwardUp, "TbArrowForwardUp"),
    w(TbChevronUp, "TbChevronUp"),
    w(TbChevronDown, "TbChevronDown"),
    w(TbChevronRight, "TbChevronRight"),
    w(TbRefresh, "TbRefresh"),
    w(TbArrowsShuffle, "TbArrowsShuffle"),
    w(TbTarget, "TbTarget"),
    w(TbArrowLoopRight, "TbArrowLoopRight"),
  ],
  Business: [
    w(TbBriefcase, "TbBriefcase"),
    w(TbBuilding, "TbBuilding"),
    w(TbChartBar, "TbChartBar"),
    w(TbChartLine, "TbChartLine"),
    w(TbChartPie, "TbChartPie"),
    w(TbCoin, "TbCoin"),
    w(TbCreditCard, "TbCreditCard"),
    w(TbCurrencyDollar, "TbCurrencyDollar"),
    w(TbReceipt, "TbReceipt"),
    w(TbPresentation, "TbPresentation"),
    w(TbBuildingBank, "TbBuildingBank"),
    w(TbWallet, "TbWallet"),
    w(TbTrendingUp, "TbTrendingUp"),
    w(TbReportMoney, "TbReportMoney"),
    w(TbFolder, "TbFolder"),
    w(TbFile, "TbFile"),
    w(TbClipboard, "TbClipboard"),
    w(TbCoins, "TbCoins"),
    w(TbCalculator, "TbCalculator"),
    w(TbPrinter, "TbPrinter"),
  ],
  Nature: [
    w(TbTree, "TbTree"),
    w(TbLeaf, "TbLeaf"),
    w(TbFlower, "TbFlower"),
    w(TbPlant, "TbPlant"),
    w(TbSeeding, "TbSeeding"),
    w(TbMountain, "TbMountain"),
    w(TbSun, "TbSun"),
    w(TbMoon, "TbMoon"),
    w(TbFlame, "TbFlame"),
    w(TbDroplet, "TbDroplet"),
    w(TbSnowflake, "TbSnowflake"),
    w(TbWorld, "TbWorld"),
    w(TbCactus, "TbCactus"),
    w(TbClover, "TbClover"),
  ],
  Animals: [
    w(TbCat, "TbCat"),
    w(TbDog, "TbDog"),
    w(TbFish, "TbFish"),
    w(TbBat, "TbBat"),
    w(TbButterfly, "TbButterfly"),
    w(TbDeer, "TbDeer"),
    w(TbHorse, "TbHorse"),
    w(TbPig, "TbPig"),
    w(TbSpider, "TbSpider"),
    w(TbFeather, "TbFeather"),
    w(TbPaw, "TbPaw"),
    w(TbBug, "TbBug"),
  ],
  Food: [
    w(TbCoffee, "TbCoffee"),
    w(TbCup, "TbCup"),
    w(TbGlass, "TbGlass"),
    w(TbPizza, "TbPizza"),
    w(TbBurger, "TbBurger"),
    w(TbIceCream, "TbIceCream"),
    w(TbCake, "TbCake"),
    w(TbApple, "TbApple"),
    w(TbCarrot, "TbCarrot"),
    w(TbBread, "TbBread"),
    w(TbEgg, "TbEgg"),
    w(TbMeat, "TbMeat"),
    w(TbSalad, "TbSalad"),
    w(TbSoup, "TbSoup"),
  ],
  Travel: [
    w(TbPlane, "TbPlane"),
    w(TbCar, "TbCar"),
    w(TbBus, "TbBus"),
    w(TbTrain, "TbTrain"),
    w(TbBike, "TbBike"),
    w(TbShip, "TbShip"),
    w(TbMap, "TbMap"),
    w(TbMapPin, "TbMapPin"),
    w(TbLuggage, "TbLuggage"),
    w(TbBackpack, "TbBackpack"),
    w(TbCompass, "TbCompass"),
    w(TbTent, "TbTent"),
    w(TbBeach, "TbBeach"),
    w(TbRoad, "TbRoad"),
  ],
  Health: [
    w(TbHeartbeat, "TbHeartbeat"),
    w(TbStethoscope, "TbStethoscope"),
    w(TbPill, "TbPill"),
    w(TbVaccine, "TbVaccine"),
    w(TbFirstAidKit, "TbFirstAidKit"),
    w(TbActivity, "TbActivity"),
    w(TbYoga, "TbYoga"),
    w(TbRun, "TbRun"),
    w(TbWeight, "TbWeight"),
    w(TbMedicalCross, "TbMedicalCross"),
    w(TbDental, "TbDental"),
    w(TbMoodHappy, "TbMoodHappy"),
    w(TbBandage, "TbBandage"),
    w(TbThermometer, "TbThermometer"),
    w(TbVirus, "TbVirus"),
    w(TbBrain, "TbBrain"),
    w(TbAmbulance, "TbAmbulance"),
    w(TbWheelchair, "TbWheelchair"),
  ],
  Tech: [
    w(TbDeviceLaptop, "TbDeviceLaptop"),
    w(TbDeviceMobile, "TbDeviceMobile"),
    w(TbDeviceDesktop, "TbDeviceDesktop"),
    w(TbCpu, "TbCpu"),
    w(TbDatabase, "TbDatabase"),
    w(TbCloud, "TbCloud"),
    w(TbWifi, "TbWifi"),
    w(TbBluetooth, "TbBluetooth"),
    w(TbCode, "TbCode"),
    w(TbTerminal, "TbTerminal"),
    w(TbRobot, "TbRobot"),
    w(TbDeviceWatch, "TbDeviceWatch"),
    w(TbUsb, "TbUsb"),
    w(TbServer, "TbServer"),
  ],
  Social: [
    w(TbBrandTwitter, "TbBrandTwitter"),
    w(TbBrandInstagram, "TbBrandInstagram"),
    w(TbBrandFacebook, "TbBrandFacebook"),
    w(TbBrandYoutube, "TbBrandYoutube"),
    w(TbBrandLinkedin, "TbBrandLinkedin"),
    w(TbBrandTiktok, "TbBrandTiktok"),
    w(TbBrandGithub, "TbBrandGithub"),
    w(TbBrandWhatsapp, "TbBrandWhatsapp"),
    w(TbBrandDiscord, "TbBrandDiscord"),
    w(TbBrandSnapchat, "TbBrandSnapchat"),
    w(TbBrandPinterest, "TbBrandPinterest"),
    w(TbMessageCircle, "TbMessageCircle"),
    w(TbShare, "TbShare"),
    w(TbThumbUp, "TbThumbUp"),
    w(TbMail, "TbMail"),
    w(TbPhoneCall, "TbPhoneCall"),
    w(TbAt, "TbAt"),
  ],
  Weather: [
    w(TbCloudRain, "TbCloudRain"),
    w(TbCloudSnow, "TbCloudSnow"),
    w(TbCloudStorm, "TbCloudStorm"),
    w(TbWind, "TbWind"),
    w(TbUmbrella, "TbUmbrella"),
    w(TbTemperature, "TbTemperature"),
    w(TbBolt, "TbBolt"),
    w(TbRainbow, "TbRainbow"),
    w(TbMist, "TbMist"),
    w(TbSunset, "TbSunset"),
    w(TbSunrise, "TbSunrise"),
    w(TbCloudFog, "TbCloudFog"),
    w(TbHourglass, "TbHourglass"),
    w(TbAlarm, "TbAlarm"),
  ],
  Objects: [
    w(TbCamera, "TbCamera"),
    w(TbBook, "TbBook"),
    w(TbGift, "TbGift"),
    w(TbKey, "TbKey"),
    w(TbLock, "TbLock"),
    w(TbBulb, "TbBulb"),
    w(TbBrush, "TbBrush"),
    w(TbPalette, "TbPalette"),
    w(TbScissors, "TbScissors"),
    w(TbPaperclip, "TbPaperclip"),
    w(TbPin, "TbPin"),
    w(TbTrophy, "TbTrophy"),
    w(TbLamp, "TbLamp"),
    w(TbHanger, "TbHanger"),
  ],
  People: [
    w(TbUsers, "TbUsers"),
    w(TbUserCircle, "TbUserCircle"),
    w(TbUserPlus, "TbUserPlus"),
    w(TbUserX, "TbUserX"),
    w(TbUserCheck, "TbUserCheck"),
    w(TbUserSearch, "TbUserSearch"),
    w(TbUserShield, "TbUserShield"),
    w(TbUserStar, "TbUserStar"),
    w(TbUserEdit, "TbUserEdit"),
    w(TbFriends, "TbFriends"),
    w(TbBabyCarriage, "TbBabyCarriage"),
    w(TbBabyBottle, "TbBabyBottle"),
    w(TbGenderMale, "TbGenderMale"),
    w(TbGenderFemale, "TbGenderFemale"),
    w(TbMoodSmile, "TbMoodSmile"),
    w(TbMoodSad, "TbMoodSad"),
    w(TbMoodWink, "TbMoodWink"),
    w(TbMoodAngry, "TbMoodAngry"),
    w(TbHandStop, "TbHandStop"),
  ],
  Sports: [
    w(TbBallBasketball, "TbBallBasketball"),
    w(TbBallBaseball, "TbBallBaseball"),
    w(TbBallFootball, "TbBallFootball"),
    w(TbBallTennis, "TbBallTennis"),
    w(TbBallVolleyball, "TbBallVolleyball"),
    w(TbBallAmericanFootball, "TbBallAmericanFootball"),
    w(TbBallBowling, "TbBallBowling"),
    w(TbGolf, "TbGolf"),
    w(TbSwimming, "TbSwimming"),
    w(TbSkateboard, "TbSkateboard"),
    w(TbBarbell, "TbBarbell"),
    w(TbMedal, "TbMedal"),
    w(TbWalk, "TbWalk"),
    w(TbStretching, "TbStretching"),
    w(TbChess, "TbChess"),
    w(TbDice, "TbDice"),
    w(TbAward, "TbAward"),
    w(TbPuzzle, "TbPuzzle"),
  ],
  Shopping: [
    w(TbBasket, "TbBasket"),
    w(TbBuildingStore, "TbBuildingStore"),
    w(TbDiscount, "TbDiscount"),
    w(TbShoppingBag, "TbShoppingBag"),
    w(TbShoppingCart, "TbShoppingCart"),
    w(TbTag, "TbTag"),
    w(TbTags, "TbTags"),
    w(TbBarcode, "TbBarcode"),
    w(TbTicket, "TbTicket"),
    w(TbQrcode, "TbQrcode"),
    w(TbBuildingWarehouse, "TbBuildingWarehouse"),
  ],
  Media: [
    w(TbHeadphones, "TbHeadphones"),
    w(TbMicrophone, "TbMicrophone"),
    w(TbMovie, "TbMovie"),
    w(TbMusic, "TbMusic"),
    w(TbPhoto, "TbPhoto"),
    w(TbVideo, "TbVideo"),
    w(TbPlayerPlay, "TbPlayerPlay"),
    w(TbPlayerPause, "TbPlayerPause"),
    w(TbVolume, "TbVolume"),
    w(TbBroadcast, "TbBroadcast"),
    w(TbDeviceTv, "TbDeviceTv"),
    w(TbPlaylist, "TbPlaylist"),
    w(TbFileMusic, "TbFileMusic"),
    w(TbDisc, "TbDisc"),
  ],
  Education: [
    w(TbSchool, "TbSchool"),
    w(TbAbacus, "TbAbacus"),
    w(TbAtom, "TbAtom"),
    w(TbFlask, "TbFlask"),
    w(TbMicroscope, "TbMicroscope"),
    w(TbPencil, "TbPencil"),
    w(TbRuler, "TbRuler"),
    w(TbCertificate, "TbCertificate"),
    w(TbBooks, "TbBooks"),
    w(TbLanguage, "TbLanguage"),
    w(TbNotebook, "TbNotebook"),
  ],
  TextEditing: [
    w(TbBold, "TbBold"),
    w(TbItalic, "TbItalic"),
    w(TbUnderline, "TbUnderline"),
    w(TbStrikethrough, "TbStrikethrough"),
    w(TbAlignLeft, "TbAlignLeft"),
    w(TbAlignCenter, "TbAlignCenter"),
    w(TbList, "TbList"),
    w(TbQuote, "TbQuote"),
    w(TbHighlight, "TbHighlight"),
    w(TbTypography, "TbTypography"),
    w(TbSubscript, "TbSubscript"),
    w(TbSuperscript, "TbSuperscript"),
  ],
  Home: [
    w(TbDoor, "TbDoor"),
    w(TbBed, "TbBed"),
    w(TbBath, "TbBath"),
    w(TbSofa, "TbSofa"),
    w(TbPlug, "TbPlug"),
    w(TbHammer, "TbHammer"),
    w(TbTools, "TbTools"),
    w(TbWindow, "TbWindow"),
    w(TbBoxSeam, "TbBoxSeam"),
    w(TbPaint, "TbPaint"),
  ],
  Symbols: [
    w(TbCircle, "TbCircle"),
    w(TbSquare, "TbSquare"),
    w(TbTriangle, "TbTriangle"),
    w(TbHexagon, "TbHexagon"),
    w(TbDiamond, "TbDiamond"),
    w(TbInfinity, "TbInfinity"),
    w(TbX, "TbX"),
    w(TbMinus, "TbMinus"),
    w(TbQuestionMark, "TbQuestionMark"),
    w(TbExclamationMark, "TbExclamationMark"),
    w(TbPlusMinus, "TbPlusMinus"),
    w(TbEqual, "TbEqual"),
    w(TbPercentage, "TbPercentage"),
    w(TbHash, "TbHash"),
  ],
};

export default iconLibraryCategories;
